import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { A, fonts, tokens } from "../lib/theme";

const TYPE_CFG = {
  success: { color: A.forest,  bg: "rgba(30,77,53,0.10)",  icon: "✓" },
  warning: { color: "#8b6020", bg: "rgba(139,96,32,0.10)", icon: "!" },
  error:   { color: A.crimson, bg: "rgba(139,26,26,0.10)", icon: "✕" },
  info:    { color: A.navy,    bg: "rgba(26,58,92,0.10)",  icon: "i" },
};

const variants = {
  initial: { x: 60, opacity: 0, scale: 0.95 },
  animate: { x: 0,  opacity: 1, scale: 1    },
  exit:    { x: 60, opacity: 0, scale: 0.97 },
};

// Defense in depth: a toast's title/message must be renderable text. If an object slips
// through (e.g. an unhandled API error body), coerce it to a string so React never throws
// error #31 ("Objects are not valid as a React child") and blanks the page.
const toText = (v) => {
  if (v == null || typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    if (typeof v.message === "string") return v.message;
    if (typeof v.error === "string") return v.error;
    if (typeof v.detail === "string") return v.detail;
    try { return JSON.stringify(v); } catch (_) { return String(v); }
  }
  return String(v);
};

export const Toast = ({ id, title, message, type = "success", onClose, duration = 7000 }) => {
  // Pause the dismiss timer while the pointer is over the toast, so a user who is
  // still reading never has it vanish mid-sentence. Leaving resets the full timer.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => onClose(id), duration);
    return () => clearTimeout(t);
  }, [id, onClose, duration, paused]);

  const cfg = TYPE_CFG[type] || TYPE_CFG.info;
  const safeTitle = toText(title);
  const safeMessage = toText(message);

  return (
    <motion.div
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ type: "spring", stiffness: 320, damping: 28 }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        backgroundColor: A.card,
        border: tokens.border.thin,
        borderLeft: `4px solid ${cfg.color}`,
        borderRadius: tokens.radius.card,
        padding: "0.85rem 1rem",
        marginBottom: "0.55rem",
        boxShadow: tokens.shadow.hover,
        display: "flex",
        alignItems: "flex-start",
        gap: "0.75rem",
        width: "auto",
        maxWidth: "min(320px, calc(100vw - 2rem))",
        fontFamily: fonts.body,
      }}
    >
      <div style={{
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.color}`,
        color: cfg.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.72rem",
        fontWeight: 800,
        flexShrink: 0,
        marginTop: "1px",
      }}>
        {cfg.icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {safeTitle && (
          <div style={{
            fontWeight: 700,
            fontSize: "0.84rem",
            color: A.dark,
            marginBottom: safeMessage ? "0.2rem" : 0,
            fontFamily: fonts.display,
          }}>
            {safeTitle}
          </div>
        )}
        {safeMessage && (
          <div style={{ fontSize: "0.79rem", color: A.muted, lineHeight: 1.45 }}>
            {safeMessage}
          </div>
        )}
      </div>

      <button
        onClick={() => onClose(id)}
        aria-label="Close"
        style={{
          background: "none",
          border: "none",
          color: A.muted,
          cursor: "pointer",
          padding: "0",
          lineHeight: 1,
          fontSize: "1.05rem",
          flexShrink: 0,
          marginTop: "-1px",
          opacity: 0.65,
          fontFamily: "inherit",
        }}
      >
        ×
      </button>
    </motion.div>
  );
};

const ToastContainer = ({ toasts, onClose }) => (
  <div style={{
    position: "fixed",
    right: "1rem",
    left: "1rem",
    top: "1rem",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    pointerEvents: "none",
  }}>
    <AnimatePresence>
      {toasts.map(t => (
        <div key={t.id} style={{ pointerEvents: "auto" }}>
          <Toast {...t} onClose={onClose} />
        </div>
      ))}
    </AnimatePresence>
  </div>
);

export default ToastContainer;
